import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import { breakpoints } from '@shared/ui/atoms/MediaQueries';
import {
  DailyFilter,
  DEFAULT_DAILY_FILTER,
  PageView,
  ViewMode,
} from '@features/whereabouts/pages/whereabouts-v2/types';

const STORAGE_KEYS = {
  viewMode: 'whereabouts-v2-view-mode',
  pageView: 'whereabouts-v2-page-view',
  weekPageView: 'whereabouts-v2-week-page-view',
  sidebarOpen: 'whereabouts-v2-sidebar-open',
  schengenCountsVisible: 'whereabouts-v2-schengen-counts-visible',
} as const;

export interface WhereaboutsViewState {
  viewMode: ViewMode;
  pageView: PageView;
  // The user's chosen page view for week/day. Month view forces pageView to
  // Calendar, so this preserves the Grid/Calendar choice to restore on return.
  weekPageView: PageView;
  dailyFilter: DailyFilter;
  sidebarOpen: boolean;
  notesPanelOpen: boolean;
  globalHeaderCollapsed: boolean;
  schengenCountsVisible: boolean;
}

function readEnum<T extends string>(key: string, values: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const saved = window.localStorage.getItem(key);
  return values.includes(saved as T) ? (saved as T) : fallback;
}

function readSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  if (window.innerWidth < breakpoints.tablet) return false;
  const saved = window.localStorage.getItem(STORAGE_KEYS.sidebarOpen);
  return saved === null ? true : saved === 'true';
}

function readSchengenCountsVisible(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(STORAGE_KEYS.schengenCountsVisible) !== 'false';
}

const storedViewMode = readEnum(STORAGE_KEYS.viewMode, Object.values(ViewMode), ViewMode.Week);
const storedPageView = readEnum(STORAGE_KEYS.pageView, Object.values(PageView), PageView.Grid);
// Fall back to the active page view so the preference is sensible before the
// week-page-view key has ever been written.
const storedWeekPageView = readEnum(STORAGE_KEYS.weekPageView, Object.values(PageView), storedPageView);

export const initialWhereaboutsViewState: WhereaboutsViewState = {
  // Month view only exists for the calendar page view
  viewMode: storedPageView === PageView.Grid && storedViewMode === ViewMode.Month ? ViewMode.Week : storedViewMode,
  pageView: storedPageView,
  weekPageView: storedWeekPageView,
  dailyFilter: DEFAULT_DAILY_FILTER,
  sidebarOpen: readSidebarOpen(),
  notesPanelOpen: false,
  globalHeaderCollapsed: false,
  schengenCountsVisible: readSchengenCountsVisible(),
};

const slice = createSlice({
  name: 'whereaboutsView',
  initialState: initialWhereaboutsViewState,
  reducers: {
    setViewMode: (state, { payload }: PayloadAction<ViewMode>) => {
      window.localStorage.setItem(STORAGE_KEYS.viewMode, payload);
      state.viewMode = payload;
      if (payload === ViewMode.Month) {
        // Month view only exists for the calendar page view. weekPageView keeps
        // the user's Grid/Calendar choice so it survives the round-trip.
        state.pageView = PageView.Calendar;
      } else {
        // Returning to week/day: restore the user's chosen page view.
        state.pageView = state.weekPageView;
      }
      window.localStorage.setItem(STORAGE_KEYS.pageView, state.pageView);
    },
    setPageView: (state, { payload }: PayloadAction<PageView>) => {
      window.localStorage.setItem(STORAGE_KEYS.pageView, payload);
      window.localStorage.setItem(STORAGE_KEYS.weekPageView, payload);
      state.pageView = payload;
      state.weekPageView = payload;
      if (payload === PageView.Grid && state.viewMode === ViewMode.Month) {
        state.viewMode = ViewMode.Week;
        window.localStorage.setItem(STORAGE_KEYS.viewMode, state.viewMode);
      }
    },
    setDailyFilter: (state, { payload }: PayloadAction<DailyFilter>) => {
      state.dailyFilter = payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
      window.localStorage.setItem(STORAGE_KEYS.sidebarOpen, String(state.sidebarOpen));
    },
    setNotesPanelOpen: (state, { payload }: PayloadAction<boolean>) => {
      state.notesPanelOpen = payload;
    },
    toggleNotesPanel: (state) => {
      state.notesPanelOpen = !state.notesPanelOpen;
    },
    setGlobalHeaderCollapsed: (state, { payload }: PayloadAction<boolean>) => {
      state.globalHeaderCollapsed = payload;
    },
    toggleSchengenCounts: (state) => {
      state.schengenCountsVisible = !state.schengenCountsVisible;
      window.localStorage.setItem(STORAGE_KEYS.schengenCountsVisible, String(state.schengenCountsVisible));
    },
  },
});

export default slice.reducer;
export const {
  setViewMode,
  setPageView,
  setDailyFilter,
  toggleSidebar,
  setNotesPanelOpen,
  toggleNotesPanel,
  setGlobalHeaderCollapsed,
  toggleSchengenCounts,
} = slice.actions;

export const selectViewMode = (state: AppState) => state.whereaboutsView.viewMode;
export const selectPageView = (state: AppState) => state.whereaboutsView.pageView;
export const selectDailyFilter = (state: AppState) => state.whereaboutsView.dailyFilter;
export const selectSidebarOpen = (state: AppState) => state.whereaboutsView.sidebarOpen;
export const selectNotesPanelOpen = (state: AppState) => state.whereaboutsView.notesPanelOpen;
export const selectGlobalHeaderCollapsed = (state: AppState) => state.whereaboutsView.globalHeaderCollapsed;
export const selectSchengenCountsVisible = (state: AppState) => state.whereaboutsView.schengenCountsVisible;
export const selectIsMonthView = (state: AppState) =>
  state.whereaboutsView.pageView === PageView.Calendar
  && state.whereaboutsView.viewMode === ViewMode.Month;
